import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import * as XLSX from 'xlsx';
import { useAuth } from '../hooks/useAuth';
import '../styles/MedicalExamExport.css';

const MedicalExamExport = () => {
  const { hasPermission, token } = useAuth();

  if (!hasPermission('/medical-exam-export', 'View')) {
    return (
      <div className="exam-export">
        <div className="access-denied">
          <h2>Access Denied</h2>
          <p>You don't have permission to view the Medical Exam Export.</p>
        </div>
      </div>
    );
  }

  const [assessments, setAssessments] = useState([]);
  const [assessmentSearch, setAssessmentSearch] = useState('');
  const [selectedAssessmentId, setSelectedAssessmentId] = useState('');
  const [status, setStatus] = useState('All');
  const [branch, setBranch] = useState('');

  const [previewData, setPreviewData] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState('');

  const [downloadingFiles, setDownloadingFiles] = useState(false);

  const [aiReviews, setAiReviews] = useState({});
  const [aiReviewLoading, setAiReviewLoading] = useState(false);
  const [aiReviewError, setAiReviewError] = useState('');

  useEffect(() => {
    const fetchAssessments = async () => {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_SERVER_BASE_URL}/hospital-portal/api/medicalreports/medical-contract/`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );

        const activeAssessments = (res.data || []).filter(
          (item) => (item?.status || '').toString().trim().toLowerCase() === 'active'
        );

        const sorted = activeAssessments.sort((a, b) => {
          const aName = (a.assessment_name || a.customer_name || '').toLowerCase();
          const bName = (b.assessment_name || b.customer_name || '').toLowerCase();
          return aName.localeCompare(bName);
        });

        setAssessments(sorted);
      } catch (error) {
        console.error('Failed to load assessments:', error);
      }
    };

    fetchAssessments();
  }, [token]);

  const selectedAssessment = assessments.find(
    (item) => String(item.id) === String(selectedAssessmentId)
  );

  const handlePreview = useCallback(async () => {
    if (!selectedAssessmentId) return;

    setPreviewLoading(true);
    setPreviewError('');
    setPreviewData(null);
    setAiReviews({});
    setAiReviewError('');

    try {
      const res = await axios.get(
        `${process.env.REACT_APP_SERVER_BASE_URL}/hospital-portal/api/medicalreports/medical-contract/report/${selectedAssessmentId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          params: {
            status,
            branch: branch || null,
          },
        }
      );

      setPreviewData(res.data);
    } catch (error) {
      setPreviewError(error.response?.data?.error || 'Failed to load preview.');
    } finally {
      setPreviewLoading(false);
    }
  }, [selectedAssessmentId, status, branch, token]);

  const handleAiReview = useCallback(async () => {
    if (!selectedAssessmentId || !previewData?.rows?.length) return;

    setAiReviewLoading(true);
    setAiReviewError('');

    try {
      const res = await axios.post(
        `${process.env.REACT_APP_SERVER_BASE_URL}/hospital-portal/api/medicalreports/medical-contract/ai-review/${selectedAssessmentId}`,
        {
          status,
          branch: branch || null,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
          timeout: 600000,
        }
      );

      setAiReviews(res.data?.reviews || {});
      if (res.data?.errors && Object.keys(res.data.errors).length > 0) {
        setAiReviewError('Some AI reviews could not be completed. See individual rows marked Error.');
      }
    } catch (error) {
      setAiReviewError(error.response?.data?.error || 'Failed to run AI review.');
    } finally {
      setAiReviewLoading(false);
    }
  }, [selectedAssessmentId, previewData, status, branch, token]);

  const handleDownloadFiles = useCallback(async () => {
    if (!selectedAssessmentId) return;

    setDownloadingFiles(true);

    try {
      const res = await axios.post(
        `${process.env.REACT_APP_SERVER_BASE_URL}/hospital-portal/api/medicalreports/medical-contract/download-files/${selectedAssessmentId}`,
        {
          status,
          branch: branch || null,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob',
          timeout: 600000,
        }
      );

      const contentDisposition = res.headers['content-disposition'] || '';
      const filenameMatch = contentDisposition.match(/filename="?([^";]+)"?/i);
      const filename = filenameMatch?.[1] || `assessment-files-${selectedAssessmentId}.zip`;

      const blob = new Blob([res.data], { type: res.headers['content-type'] || 'application/zip' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      alert(error.response?.data?.error || 'Failed to download files.');
    } finally {
      setDownloadingFiles(false);
    }
  }, [selectedAssessmentId, status, branch, token]);

  const handleExcelExport = () => {
    if (!previewData?.rows?.length) {
      alert('No preview data to export.');
      return;
    }

    const includesAudiometry = !!previewData?.assessment?.includes_audiometry;
    const sheetRows = previewData.rows.map((row, index) => {
      const baseRow = {
        '#': index + 1,
        'Patient Number': row.patient_number,
        'Emp ID': row.company_id || '',
        'Patient Name': row.patient_name,
        'Visit Date': row.visit_date,
        Branch: row.branch || '',
        'Lab Status': row.lab_status,
        'Radiology Status': row.radiology_status,
        'Doctor Assessment': row.doctor_assessment,
        'Final Assessment': getFinalAssessment(row.doctor_assessment),
        'Overall Status': row.overall_status,
        'AI Review': aiReviews[row.visit_id] || '',
      };

      if (includesAudiometry) {
        baseRow.Audiometry = row.audiometry_result;
      }

      return baseRow;
    });

    const worksheet = XLSX.utils.json_to_sheet(sheetRows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Preview');

    const assessmentLabel = (
      selectedAssessment?.assessment_code ||
      selectedAssessment?.assessment_name ||
      selectedAssessment?.customer_name ||
      'assessment'
    )
      .toString()
      .replace(/[^a-zA-Z0-9_\-]/g, '_');

    XLSX.writeFile(workbook, `medical_assessment_preview_${assessmentLabel}.xlsx`);
  };

  const filteredAssessments = assessments.filter((item) => {
    const text = `${item.assessment_name || ''} ${item.assessment_code || ''} ${item.customer_name || ''}`.toLowerCase();
    return text.includes(assessmentSearch.toLowerCase());
  });

  const getStatusBadge = (value) => {
    const map = {
      Complete: 'badge-success',
      'Ready for Doctor Assessment': 'badge-warning',
      'Pending Tests': 'badge-danger',
      'Fit for work': 'badge-success',
      Pass: 'badge-success',
      Error: 'badge-danger',
      Review: 'badge-warning',
      complete: 'badge-success',
      paid: 'badge-info',
      unpaid: 'badge-danger',
      canceled: 'badge-secondary',
      no_request: 'badge-secondary',
      Invoiced: 'badge-success',
      'Not Invoiced': 'badge-warning',
      Pending: 'badge-warning',
      'Missing ECG': 'badge-warning',
      'Missing XRay': 'badge-warning',
    };

    return <span className={`badge ${map[value] || 'badge-secondary'}`}>{value}</span>;
  };

  const getFinalAssessment = (doctorAssessment) => {
    const normalized = (doctorAssessment || '').toString().trim();

    if (!normalized) {
      return 'Pending';
    }

    if (normalized.toLowerCase().includes('fit for work') || normalized.toLowerCase().includes('fit to work')) {
      return 'Fit for work';
    }

    return 'Review';
  };

  const renderAiReview = (visitId) => {
    const review = aiReviews[visitId];
    if (!review) return '—';
    if (review === 'Pass') return getStatusBadge('Pass');
    if (review === 'Error') return getStatusBadge('Error');
    return <span className="ai-review-text">{review}</span>;
  };

  const includesAudiometry = !!previewData?.assessment?.includes_audiometry;

  return (
    <div className="exam-export">
      <div className="export-header">
        <h1>Medical Assessment Export</h1>
        <p className="header-subtitle">
          Select a medical assessment, preview the matching employee results, run an AI review on exam vitals and labs,
          export the preview to Excel, or download only employee files as ZIP.
        </p>
      </div>

      <div className="export-filters">
        <div className="filter-group assessment-group">
          <label>Medical Assessment *</label>
          <input
            type="text"
            className="customer-search"
            placeholder="Search by assessment name/code/customer..."
            value={assessmentSearch}
            onChange={(e) => setAssessmentSearch(e.target.value)}
          />
          <select
            className="status-select"
            value={selectedAssessmentId}
            onChange={(e) => setSelectedAssessmentId(e.target.value)}
          >
            <option value="">Select assessment</option>
            {filteredAssessments.map((item) => (
              <option key={item.id} value={item.id}>
                {(item.assessment_name || 'Unnamed Assessment')} {item.assessment_code ? `(${item.assessment_code})` : ''}
              </option>
            ))}
          </select>
          {selectedAssessment && (
            <span className="selected-company">
              ✓ {selectedAssessment.assessment_name || selectedAssessment.customer_name} {selectedAssessment.assessment_code ? `(${selectedAssessment.assessment_code})` : ''}
            </span>
          )}
        </div>

        <div className="filter-group">
          <label>Status</label>
          <select className="status-select" value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="All">All</option>
            <option value="Complete">Complete</option>
            <option value="Ready for Doctor Assessment">Ready for Doctor Assessment</option>
            <option value="Pending Tests">Pending Tests</option>
          </select>
        </div>

        <div className="filter-group">
          <label>Branch (optional)</label>
          <input
            type="text"
            className="branch-input"
            placeholder="e.g. Main"
            value={branch}
            onChange={(e) => setBranch(e.target.value)}
          />
        </div>

        <div className="filter-actions">
          <button className="btn-preview" onClick={handlePreview} disabled={!selectedAssessmentId || previewLoading}>
            {previewLoading ? 'Loading…' : 'Preview'}
          </button>

          <button className="btn-download" onClick={handleExcelExport} disabled={!previewData?.rows?.length}>
            Download Preview (Excel)
          </button>

          <button
            className="btn-download btn-download-html"
            onClick={handleDownloadFiles}
            disabled={!selectedAssessmentId || downloadingFiles}
          >
            {downloadingFiles ? 'Preparing Files…' : 'Download Employee Files'}
          </button>
        </div>
      </div>

      {previewError && (
        <div className="export-error">
          <span className="error-icon">⚠️</span> {previewError}
        </div>
      )}

      {aiReviewError && (
        <div className="export-error">
          <span className="error-icon">⚠️</span> {aiReviewError}
        </div>
      )}

      {previewData && (
        <div className="preview-section">
          <div className="preview-header">
            <h2>
              Preview — {previewData.count} patient{previewData.count !== 1 ? 's' : ''}
            </h2>
            <div className="preview-header-actions">
              <span className="preview-info">Excel export includes exactly these preview rows.</span>
              {previewData.count > 0 && (
                <button
                  className="btn-ai-review"
                  onClick={handleAiReview}
                  disabled={aiReviewLoading}
                >
                  {aiReviewLoading
                    ? `Reviewing ${previewData.count} patient${previewData.count !== 1 ? 's' : ''}…`
                    : 'Run AI Review'}
                </button>
              )}
            </div>
          </div>

          {previewData.count === 0 ? (
            <div className="empty-state">
              <p>No records found for the selected assessment and filters.</p>
            </div>
          ) : (
            <div className="table-wrapper">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Patient Number</th>
                    <th>Emp ID</th>
                    <th>Patient Name</th>
                    <th>Visit Date</th>
                    <th>Branch</th>
                    <th>Lab Status</th>
                    <th>Radiology</th>
                    {includesAudiometry && <th>Audiometry</th>}
                    <th>Doctor Assessment</th>
                    <th>Final Assessment</th>
                    <th>Overall</th>
                    <th>AI Review</th>
                  </tr>
                </thead>
                <tbody>
                  {previewData.rows.map((row, idx) => (
                    <tr key={`${row.visit_id}-${idx}`}>
                      <td>{idx + 1}</td>
                      <td>{row.patient_number}</td>
                      <td>{row.company_id || '—'}</td>
                      <td><strong>{row.patient_name}</strong></td>
                      <td>{row.visit_date}</td>
                      <td>{row.branch || '—'}</td>
                      <td>{getStatusBadge(row.lab_status)}</td>
                      <td>{getStatusBadge(row.radiology_status)}</td>
                      {includesAudiometry && (
                        <td>
                          {row.audiometry_result === 'Pending'
                            ? getStatusBadge('Pending')
                            : row.audiometry_result}
                        </td>
                      )}
                      <td className="doctor-assessment-cell">
                        {row.doctor_assessment === 'Pending'
                          ? getStatusBadge('Pending')
                          : row.doctor_assessment}
                      </td>
                      <td>{getStatusBadge(getFinalAssessment(row.doctor_assessment))}</td>
                      <td>{getStatusBadge(row.overall_status)}</td>
                      <td className="ai-review-cell">{renderAiReview(row.visit_id)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      <div className="info-box">
        <h3>What this page exports</h3>
        <ul>
          <li><strong>Run AI Review</strong> analyzes visit vitals and lab results for each previewed employee (no radiology).</li>
          <li><strong>Download Preview (Excel)</strong> exports only the currently previewed rows.</li>
          <li><strong>Download Employee Files</strong> exports employee attachments plus generated lab report PDFs in ZIP, with type-labeled file names (for example x-ray, ecg, lab).</li>
        </ul>
      </div>
    </div>
  );
};

export default MedicalExamExport;
